import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getDashboard, getRecommendations, fmtMoney, fmtNum, fmtPct } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  CurrencyDollar, ShoppingCart, ChartLineUp, Receipt, UsersThree, Target,
  Sparkle, ArrowsClockwise, Lightning,
} from "@phosphor-icons/react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, LineChart, Line,
} from "recharts";

const chartTip = {
  contentStyle: { background: "#10101A", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#fff" },
  labelStyle: { color: "#9a9a9a" },
};
const axis = { stroke: "#5a5a5a", fontSize: 11, tickLine: false, axisLine: false };

export default function Dashboard() {
  const { activeId, activeClient } = useClient();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", activeId], queryFn: () => getDashboard(activeId), enabled: !!activeId,
  });
  const recQ = useQuery({
    queryKey: ["recommendations", activeId], queryFn: () => getRecommendations(activeId),
    enabled: !!activeId, staleTime: 5 * 60 * 1000,
  });

  if (isLoading || !data) {
    return <PageContainer><div className="text-[#7a7a7a] py-20 text-center">Loading command center…</div></PageContainer>;
  }

  const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <PageContainer>
      <PageHeader eyebrow={`Command Center · ${activeClient?.name || ""}`} title="Marketing Dashboard"
        subtitle="Your live growth snapshot across revenue, acquisition, retention and paid performance." />

      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        <StatCard label="Weekly Revenue" value={fmtMoney(data.revenue)} delta={data.revenue_delta} icon={CurrencyDollar} accent />
        <StatCard label="Orders" value={fmtNum(data.orders)} delta={data.orders_delta} icon={ShoppingCart} />
        <StatCard label="Avg Order Value" value={fmtMoney(data.aov)} delta={data.aov_delta} icon={Receipt} />
        <StatCard label="Conversion Rate" value={fmtPct(data.conversion_rate)} delta={data.conversion_delta} icon={ChartLineUp} />
        <StatCard label="Blended ROAS" value={`${data.roas}x`} icon={Target} accent />
        <StatCard label="Active Subscribers" value={fmtNum(data.active_subscribers)} delta={data.subscribers_delta} icon={UsersThree} />
        <StatCard label="Repeat Rate" value={fmtPct(data.repeat_rate)} icon={ArrowsClockwise} />
        <StatCard label="Email Revenue" value={fmtMoney(data.email_revenue)} icon={Lightning} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mt-5">
        <div className="card-matte p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div><div className="label-caps">Revenue Trend</div><div className="font-head text-lg mt-1">Weekly revenue & orders</div></div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_series}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6B3DFF" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#6B3DFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
              <YAxis {...axis} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip {...chartTip} formatter={(v, n) => [n === "revenue" ? fmtMoney(v) : v, n]} labelFormatter={fmtDate} />
              <Area type="monotone" dataKey="revenue" stroke="#6B3DFF" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card-matte p-5">
          <div className="label-caps mb-1">ROAS by Channel</div>
          <div className="font-head text-lg mb-4">Paid efficiency</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.roas_by_channel} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" horizontal={false} />
              <XAxis type="number" {...axis} />
              <YAxis type="category" dataKey="channel" {...axis} width={90} tick={{ fontSize: 10, fill: "#9a9a9a" }} />
              <Tooltip {...chartTip} formatter={(v) => [`${v}x`, "ROAS"]} />
              <Bar dataKey="roas" fill="#6B3DFF" radius={[0, 4, 4, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AI Recommendations + progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mt-5">
        <div className="lg:col-span-2 card-matte brand-glow p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkle size={20} className="brand-text" weight="fill" />
              <div className="font-head text-lg">AI Manager · Next Best Actions</div>
            </div>
            <Button data-testid="refresh-recs-btn" variant="ghost" size="sm" onClick={() => recQ.refetch()} className="text-[#9a9a9a] hover:text-white">
              <ArrowsClockwise size={16} className={recQ.isFetching ? "ai-thinking" : ""} />
            </Button>
          </div>
          {recQ.isLoading || recQ.isFetching ? (
            <div className="text-[#9a9a9a] py-8 text-center ai-thinking">Analyzing your data…</div>
          ) : (
            <div className="space-y-3">
              {recQ.data?.recommendations?.map((r, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/8" data-testid={`rec-${i}`}>
                  <div className="mt-0.5"><StatusBadge value={r.priority} /></div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm flex items-center gap-2">{r.title}
                      <span className="text-[10px] text-[#7a7a7a] uppercase tracking-wider">{r.category}</span></div>
                    <div className="text-sm text-[#9a9a9a] mt-0.5">{r.reason}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="card-matte p-5">
            <div className="label-caps mb-3">30-Day Plan Progress</div>
            <div className="flex items-end justify-between mb-2">
              <div className="font-head text-3xl">{data.plan_pct}%</div>
              <div className="text-sm text-[#9a9a9a]">{data.plan_done}/{data.plan_total} tasks</div>
            </div>
            <Progress value={data.plan_pct} className="h-2 bg-white/10 [&>div]:bg-[#6B3DFF]" />
          </div>
          <div className="card-matte p-5">
            <div className="label-caps mb-3">Conversion Checklist</div>
            <div className="flex items-end justify-between mb-2">
              <div className="font-head text-3xl">{data.checklist_pct}%</div>
              <div className="text-sm text-[#9a9a9a]">{data.checklist_done}/{data.checklist_total} done</div>
            </div>
            <Progress value={data.checklist_pct} className="h-2 bg-white/10 [&>div]:bg-emerald-500" />
          </div>
          <div className="card-matte p-5">
            <div className="label-caps mb-3">Subscriptions</div>
            <ResponsiveContainer width="100%" height={90}>
              <LineChart data={data.subscription_series}>
                <Tooltip {...chartTip} labelFormatter={fmtDate} />
                <Line type="monotone" dataKey="active_subscribers" stroke="#34D399" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            <div className="text-sm text-[#9a9a9a] mt-1">MRR <span className="text-white font-semibold">{fmtMoney(data.mrr)}</span></div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
